/**
 * https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_flatten
 * https://github.com/you-dont-need-x/you-dont-need-lodash
 */

// ----------------------------------------------------------------------

import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { usePermissions } from 'src/context/PermissionsContext';
import { hasAnyPermission } from './permission-helper';

export function flattenArray<T>(list: T[], key = 'children'): T[] {
  let children: T[] = [];

  const flatten = list?.map((item: any) => {
    if (item[key] && item[key].length) {
      children = [...children, ...item[key]];
    }
    return item;
  });

  return flatten?.concat(children.length ? flattenArray(children, key) : children);
}

// ----------------------------------------------------------------------

export function flattenDeep(array: any): any[] {
  const isArray = array && Array.isArray(array);

  if (isArray) {
    return array.flat(Infinity);
  }
  return [];
}

// ----------------------------------------------------------------------

export function orderBy<T>(array: T[], properties: (keyof T)[], orders?: ('asc' | 'desc')[]): T[] {
  return array.slice().sort((a, b) => {
    for (let i = 0; i < properties.length; i += 1) {
      const property = properties[i];
      const order = orders && orders[i] === 'desc' ? -1 : 1;

      const aValue = a[property];
      const bValue = b[property];

      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
    }
    return 0;
  });
}

// ----------------------------------------------------------------------

export function keyBy<T>(
  array: T[],
  key: keyof T
): {
  [key: string]: T;
} {
  return (array || []).reduce((result, item) => {
    const keyValue = key ? item[key] : item;

    return { ...result, [String(keyValue)]: item };
  }, {});
}

// ----------------------------------------------------------------------

export function sumBy<T>(array: T[], iteratee: (item: T) => number): number {
  return array.reduce((sum, item) => sum + iteratee(item), 0);
}

// ----------------------------------------------------------------------

export function isEqual(a: any, b: any): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (typeof a === 'string' || typeof a === 'number' || typeof a === 'boolean') {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a!);
    const keysB = Object.keys(b!);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => isEqual(a[key], b[key]));
  }

  return false;
}

// ----------------------------------------------------------------------

function isObject(item: any) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export const merge = (target: any, ...sources: any[]): any => {
  if (!sources.length) return target;

  const source = sources.shift();

  // eslint-disable-next-line no-restricted-syntax
  for (const key in source) {
    if (isObject(source[key])) {
      if (!target[key]) Object.assign(target, { [key]: {} });
      merge(target[key], source[key]);
    } else {
      Object.assign(target, { [key]: source[key] });
    }
  }

  return merge(target, ...sources);
};

// ----------------------------------------------------------------------

export const formatDateTime = (
  dateTimeString: string,
  dateOnly: boolean = false,
  useTimeZone: boolean = true
) => {
  // console.log(dateTimeString, 'dateTimeString');
  if (!dateTimeString) {
    return '-';
  }

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const momentObj = moment.utc(dateTimeString);

  const formattedDateString = useTimeZone
    ? momentObj.tz(userTimeZone).format(dateOnly ? 'MM-DD-YYYY' : 'MM-DD-YYYY hh:mm A z')
    : momentObj.format(dateOnly ? 'MM-DD-YYYY' : 'MM-DD-YYYY hh:mm A');

  return formattedDateString;
};

// ----------------------------------------------------------------------

export const useHasPermission = () => {
  const { permissions } = usePermissions();

  return useCallback(
    (item: any) => {
      // Check if item exists before accessing its properties
      if (!item) return true;
      
      // If no checkPermissions defined, allow access (for backward compatibility)
      // TODO: In future, we should require explicit permissions for security
      if (!item.checkPermissions || item.checkPermissions.length === 0) return true;
      
      // Use permission helper with colon-based permission system
      // This supports both new colon format and old underscore format (backward compatibility)
      return hasAnyPermission(item.checkPermissions, permissions);
    },
    [permissions] // memoization key
  );
};

/**
 * Check if user is super admin
 * Returns true if:
 * 1. User has no role (gets SUPER_ADMIN_PERMISSIONS by default from backend)
 * 2. User has a role that includes SUPER_ADMIN_PERMISSIONS in the permissions array
 * 3. User has wildcard '*' permission
 */
export const useIsSuperAdmin = () => {
  const { permissions } = usePermissions();
  return useMemo(() => {
    if (!Array.isArray(permissions)) return false;
    
    // Check for wildcard or SUPER_ADMIN_PERMISSIONS
    // This works for both:
    // - Users without roles (backend returns ['SUPER_ADMIN_PERMISSIONS'])
    // - Users with roles that have SUPER_ADMIN_PERMISSIONS assigned
    return permissions.includes('*') || permissions.includes('SUPER_ADMIN_PERMISSIONS');
  }, [permissions]);
};
